import React, { useRef, useCallback, useContext, useEffect } from 'react';
import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { Link } from '@/i18n/navigation';
import { convertTimestamp } from "@/utils/convertTimestamp";
import { canManageFeatured } from "@/lib/permissions";
import FeaturedToggle from "@/components/ui/FeaturedToggle";
import { logger } from "@/lib/logger";
import { UserContext } from "@/context/UserContext";
import { getCachedFirstFrameUrl } from '@/utils/videoUrlCache';

export interface VideoData {
  id: string;
  task_id: string;
  type: string;
  data: string;
  output: string | object;
  created_at: string;
  email: string;
  is_featured?: boolean;
  is_paid?: boolean;
}

interface VideoCardProps {
  video: VideoData;
  index: number;
  onDetailClick?: (video: VideoData, event: React.MouseEvent) => void;
  onFeaturedToggle?: (video: VideoData) => void;
  userEmail?: string;
  showFeaturedToggle?: boolean;
  showProBadge?: boolean;
  showDetailButton?: boolean;
  showShareButton?: boolean;
  videoRefs?: React.MutableRefObject<{ [key: string]: HTMLVideoElement }>;
}

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

const ExploreVideoCard: React.FC<VideoCardProps> = ({
  video,
  index,
  onDetailClick,
  onFeaturedToggle,
  userEmail,
  showFeaturedToggle = false,
  showProBadge = false,
  showDetailButton = true,
  showShareButton = true,
  videoRefs
}) => {
  const localVideoRefs = useRef<{ [key: string]: HTMLVideoElement }>({});
  const refs = videoRefs || localVideoRefs;
  const { user } = useContext(UserContext);

  // 解析视频URL的通用逻辑
  const getVideoUrl = useCallback((video: VideoData): string => {
    let videoUrl = '';

    // 首先尝试解析output字段
    if (video.output) {
      if (typeof video.output === 'string') {
        try {
          const outputData = JSON.parse(video.output);
          videoUrl = outputData.url || outputData.waterMarkedUrl || '';
        } catch (e) {
          logger.warn('Failed to parse output JSON:', e);
          // 如果不是JSON，直接使用
          videoUrl = video.output;
        }
      } else if (typeof video.output === 'object') {
        videoUrl = (video.output as any).url || (video.output as any).waterMarkedUrl || '';
      }
    }

    // 如果output中没有URL，尝试从data字段获取
    if (!videoUrl && video.data) {
      try {
        const videoData = JSON.parse(video.data);
        videoUrl = videoData.url || '';
      } catch (e) {
        logger.warn('Failed to parse data JSON:', e);
      }
    }

    logger.debug('Video URL extracted:', { taskId: video.task_id, videoUrl });
    return videoUrl;
  }, []);

  // 获取视频提示词
  const getVideoPrompt = useCallback((video: VideoData): string => {
    try {
      const videoData = JSON.parse(video.data);
      return videoData.prompt || 'No description available';
    } catch (e) {
      logger.warn('Failed to parse data for prompt:', e);
      return 'No description available';
    }
  }, []);

  // 获取图片URL
  const getImageUrl = useCallback((video: VideoData): string => {
    try {
      const videoData = JSON.parse(video.data);
      return videoData.image_url || '';
    } catch (e) {
      return '';
    }
  }, []);

  // 设置视频引用
  const setVideoRef = useCallback((element: HTMLVideoElement | null, taskId: string) => {
    if (element) {
      refs.current[taskId] = element;
    } else if (refs.current[taskId]) {
      delete refs.current[taskId];
    }
  }, [refs]);

  // 鼠标进入时播放视频
  const handleMouseEnter = useCallback(() => {
    const videoEl = refs.current[video.task_id];
    if (videoEl && videoEl.paused) {
      videoEl.muted = true;
      videoEl.play().catch(err => {
        logger.debug('Auto-play failed:', err);
      });
    }
  }, [video.task_id, refs]);

  // 鼠标离开时暂停视频
  const handleMouseLeave = useCallback(() => {
    const videoEl = refs.current[video.task_id];
    if (videoEl && !videoEl.paused) {
      videoEl.pause();
      videoEl.currentTime = 0;
    }
  }, [video.task_id, refs]);

  const videoUrl = getVideoUrl(video);
  const prompt = getVideoPrompt(video);
  const imageUrl = getImageUrl(video);



  // 生成自动封面图片作为后备选项
  const autoGeneratedThumbnail = videoUrl ? getCachedFirstFrameUrl(videoUrl) : '';

  // 优先使用原始图片，其次是自动生成的封面
  const finalImageUrl = imageUrl || autoGeneratedThumbnail;

  const canManage = canManageFeatured(userEmail);


  return (
    <motion.div
      key={`${video.task_id}-${video.id}-${index}`}
      variants={item}
      className="group relative cursor-pointer"
    >
      {/* Featured Toggle */}
      {showFeaturedToggle && canManage && onFeaturedToggle && (
        <div className="absolute top-3 left-3 opacity-0 group-hover:opacity-100 transition-all duration-300 z-50 pointer-events-auto">
          <FeaturedToggle
            isFeatured={video.is_featured || false}
            onToggle={(e) => {
              e.preventDefault();
              onFeaturedToggle(video);
            }}
          />
        </div>
      )}

      <div
        className="relative w-full overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500/20 via-green-500/10 to-teal-500/20 hover:from-emerald-400/30 hover:via-green-400/20 hover:to-teal-400/30 transition-all duration-500 border border-white/10 hover:border-emerald-400/40 hover:shadow-[0_8px_32px_rgba(16,185,129,0.15)] group-hover:shadow-[0_20px_40px_rgba(16,185,129,0.2)] video-card-container"
        style={{ aspectRatio: '16/9', minHeight: '200px' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Detail Button */}
        {showDetailButton && onDetailClick && (
          <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-300 z-50 pointer-events-auto">
            <button
              className="p-2 rounded-xl bg-black/80 hover:bg-black/90 backdrop-blur-sm text-white transition-all duration-300 hover:scale-110 shadow-lg"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDetailClick(video, e);
              }}
              title="View Details"
            >
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>
        )}


        {/* Video Element with Poster */}
        {videoUrl ? (
          <div className="relative w-full h-full">
            {/* 视频元素 - 放在最前面 */}
            <video
              ref={(element) => setVideoRef(element, video.task_id)}
              src={videoUrl}
              poster={finalImageUrl || undefined}
              data-task-id={video.task_id}
              className="w-full h-full object-cover rounded-2xl"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 10,
                pointerEvents: 'none'
              }}
              loop
              muted
              playsInline
              preload="metadata"
              controlsList="nodownload"
              onContextMenu={(e) => e.preventDefault()}
              disablePictureInPicture
              disableRemotePlayback

              onError={(e) => {
                logger.error('Video load error:', {
                  task_id: video.task_id,
                  src: videoUrl,
                  error: (e.target as HTMLVideoElement).error
                });
              }}
            />

            {/* 背景封面图片 - 作为后备 */}
            {finalImageUrl && (
              <img
                src={finalImageUrl}
                alt={prompt}
                className="w-full h-full object-cover rounded-2xl"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 0,
                  pointerEvents: 'none'
                }}
              />
            )}
          </div>
        ) : finalImageUrl ? (
          <img
            src={finalImageUrl}
            alt={prompt}
            className="w-full h-full object-cover rounded-2xl"
            style={{ pointerEvents: 'none' }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl">
            <div className="text-center">
              <div className="text-3xl mb-2 opacity-60">🎬</div>
              <p className="text-xs text-gray-400">Video not available</p>
            </div>
          </div>
        )}

        {/* Hover Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 rounded-b-2xl z-40 pointer-events-auto">
          <div className="p-4 pt-8">
            {/* Prompt Text */}
            <p className="text-white text-sm leading-relaxed mb-3 line-clamp-2">
              {prompt}
            </p>

            {/* Bottom Info and Button */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 flex items-center justify-center text-white text-xs font-bold">
                  {video.email ? video.email[0].toUpperCase() : '?'}
                </div>
                <span className="text-xs text-gray-300">
                  {convertTimestamp(video.created_at)}
                </span>
                {video.type && (
                  <span className="text-xs px-2 py-1 rounded-md bg-emerald-500/30 text-emerald-200 font-medium">
                    {/* {video.type} */}
                  </span>
                )}
              </div>

              {/* Recreate Button */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  // 直接传递素材ID到Veo3页面
                  const veo3Url = `/veo3?materialId=${encodeURIComponent(video.task_id)}`;
                  window.location.href = veo3Url;
                }}
                className="group/btn relative flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 text-white rounded-xl font-semibold text-sm transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl hover:shadow-emerald-500/25 border border-emerald-400/30 hover:border-emerald-300/50 backdrop-blur-sm"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 to-emerald-500/20 rounded-xl opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                <svg className="w-4 h-4 relative z-10 group-hover/btn:rotate-180 transition-transform duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="relative z-10 group-hover/btn:text-white transition-colors duration-300">Recreate</span>
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-white/10 to-white/5 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ExploreVideoCard; 